import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish} from 'shared/web';

import {MatrixCellTooltip} from './MatrixCellTooltip';
import {ConfiguredFinancingMatrixCellState} from './types/FinancingMatrixCellState';
import {FinancingCalculationResult} from './utils/calculateFinancingOffer';

function createMatrixTooltipStatus(label: string, state: ConfiguredFinancingMatrixCellState) {
  return {label, state};
}

function getMatrixStatusLabel(state: ConfiguredFinancingMatrixCellState) {
  return match(state)
    .with('ACTIVE', () => i18n.t('page.vehicle.financingAndLeasing.activePrimary'))
    .with('CUSTOM', () => i18n.t('page.vehicle.financingAndLeasing.manualOverride'))
    .with('CONFIGURED', () => i18n.t('page.vehicle.financingAndLeasing.configured'))
    .exhaustive();
}

interface FinancingCellTooltipProps {
  term: number;
  downPayment: number;
  /** Balloon bracket of the cell, or `null` for a standard financing cell. */
  balloonPayment: number | null;
  vehiclePrice: number;
  currency: string;
  nominalRate: string;
  apr: string;
  calculation: FinancingCalculationResult | null;
  isActive: boolean;
  isCustom: boolean;
}

/**
 * Tooltip body of one financing cell.
 *
 * The grid creates this element for every configured cell, but React only calls the function when
 * the platform `Tooltip` opens. Keeping the labels, the currency formatting and the value rows in
 * here means a matrix render costs one element per cell instead of a whole throwaway tooltip tree.
 */
export function FinancingCellTooltip(props: FinancingCellTooltipProps) {
  const formatCurrency = useFormatCurrency();
  const formatAmount = (amount: number | Nullish) =>
    isNotNil(amount)
      ? (formatCurrency(amount, props.currency) ?? EMPTY_PLACEHOLDER)
      : EMPTY_PLACEHOLDER;

  const statusStates = match<
    {isActive: boolean; isCustom: boolean},
    ConfiguredFinancingMatrixCellState[]
  >({isActive: props.isActive, isCustom: props.isCustom})
    .with({isActive: true, isCustom: true}, () => ['ACTIVE', 'CUSTOM'])
    .with({isActive: true, isCustom: false}, () => ['ACTIVE'])
    .with({isActive: false, isCustom: true}, () => ['CUSTOM'])
    .with({isActive: false, isCustom: false}, () => ['CONFIGURED'])
    .exhaustive();

  return (
    <MatrixCellTooltip
      context={[
        i18n.t('page.vehicle.financingAndLeasing.termValue', {term: props.term}),
        `${i18n.t('page.vehicle.financingAndLeasing.downPayment')}: ${props.downPayment} % (${formatAmount(props.vehiclePrice * (props.downPayment / 100))})`,
        isNotNil(props.balloonPayment)
          ? i18n.t('page.vehicle.financingAndLeasing.balloonValue', {
              balloonPayment: props.balloonPayment,
            })
          : null,
      ]
        .filter(isNotNil)
        .join(' · ')}
      headlineLabel={i18n.t('page.vehicle.financingAndLeasing.monthlyRateGross')}
      headlineValue={formatAmount(props.calculation?.monthlyRate)}
      summary={`${props.nominalRate} % ${i18n.t(
        'page.vehicle.financingAndLeasing.nominalRate'
      )} · ${props.apr} % ${i18n.t('page.vehicle.financingAndLeasing.apr')}`}
      values={[
        {
          label: i18n.t('page.vehicle.financingAndLeasing.netCredit'),
          value: formatAmount(props.calculation?.netCredit),
        },
        {
          label: i18n.t('page.vehicle.financingAndLeasing.grossCredit'),
          value: formatAmount(props.calculation?.grossCredit),
        },
        {
          label: i18n.t('page.vehicle.financingAndLeasing.totalInterest'),
          value: formatAmount(props.calculation?.totalInterest),
        },
        {
          label: i18n.t('page.vehicle.financingAndLeasing.totalAmount'),
          value: formatAmount(props.calculation?.totalAmount),
        },
      ]}
      statuses={statusStates.map((state) =>
        createMatrixTooltipStatus(getMatrixStatusLabel(state), state)
      )}
    />
  );
}
